import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CETimePicker from "components/CETimePicker";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";

/**
 * Reminder Watch settings tab.
 *
 * Rendered from Settings → Reminder Watch. Lets the member manage their
 * personal reminder-watch rules: the server scheduler checks tomorrow's
 * interpretation assignments and creates in-app notifications (type
 * `reminder_watch`) for assignments that have no ReminderSentOn yet.
 *
 * CRUD lives in server/routes/ReminderWatchRules.js:
 *   GET    /reminderWatchRules
 *   POST   /reminderWatchRules          { Name?, CheckTime, RecurEveryMinutes?, Enabled }
 *   PUT    /reminderWatchRules/:id      same body
 *   DELETE /reminderWatchRules/:id
 */
const ReminderWatchSettings = ({ theme, isMobile }) => {
  const { showSnackbar, openDialog, closeDialog } = useStateContext();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [addForm, setAddForm] = useState({
    Name: "",
    CheckTime: dayjs().hour(9).minute(0).second(0),
    RecurEveryMinutes: "",
    Enabled: true,
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    Name: "",
    CheckTime: dayjs().hour(9).minute(0).second(0),
    RecurEveryMinutes: "",
    Enabled: true,
  });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/reminderWatchRules");
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.rules || [];
      setRules(list);
    } catch (err) {
      console.error("Error fetching reminder watch rules:", err);
      showSnackbar("Unable to load reminder watch rules", theme, "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, theme]);

  // Load the rules ONCE per mount. `fetchRules` depends on `theme` (it passes
  // it to the error snackbar), and the app-wide theme object is re-created
  // whenever a snackbar opens (ContextProvider re-provides it and MUI merges
  // `{...outerTheme, ...appTheme}`), so a plain `useEffect([fetchRules])`
  // re-ran the fetch on every theme change. During an outage those re-fetches
  // failed and re-showed "Unable to load reminder watch rules" in a loop.
  // A latest-ref keeps the effect mount-only without stale-closure risk.
  const fetchRulesRef = useRef(fetchRules);

  useEffect(() => {
    fetchRulesRef.current = fetchRules;
  }, [fetchRules]);

  useEffect(() => {
    fetchRulesRef.current();
  }, []);

  // "HH:mm" (or "HH:mm:ss") → dayjs value for the time picker.
  const toTimePickerValue = useCallback((timeValue) => {
    const trimmed = String(timeValue || "").trim();
    const match = trimmed.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (!match) return dayjs().hour(9).minute(0).second(0);
    return dayjs()
      .hour(Number(match[1]))
      .minute(Number(match[2]))
      .second(0);
  }, []);

  // dayjs value → "HH:mm" for the API.
  const toTimeInputValue = useCallback((value) => {
    if (!value || typeof value.format !== "function" || !value.isValid()) {
      return "";
    }
    return value.format("HH:mm");
  }, []);

  // Validates a form and returns the API payload, or null when invalid.
  const buildPayload = (form) => {
    const checkTime = toTimeInputValue(form.CheckTime);
    if (!checkTime) {
      showSnackbar("Check time is required", theme, "error");
      return null;
    }

    const recurRaw = form.RecurEveryMinutes;
    const recurEmpty =
      recurRaw === "" || recurRaw === null || recurRaw === undefined;
    if (!recurEmpty) {
      const recurNum = Number(recurRaw);
      if (!Number.isFinite(recurNum) || recurNum < 5) {
        showSnackbar(
          "Recur every must be at least 5 minutes",
          theme,
          "error",
        );
        return null;
      }
    }

    return {
      Name: String(form.Name || "").trim() || null,
      CheckTime: checkTime,
      RecurEveryMinutes: recurEmpty ? null : Number(recurRaw),
      Enabled: form.Enabled !== false,
    };
  };

  const handleCreate = async () => {
    const payload = buildPayload(addForm);
    if (!payload) return;

    setSaving(true);
    try {
      await api.post("/reminderWatchRules", payload);
      setAddForm({
        Name: "",
        CheckTime: dayjs().hour(9).minute(0).second(0),
        RecurEveryMinutes: "",
        Enabled: true,
      });
      showSnackbar("Reminder watch rule created", theme, "success");
      fetchRules();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || "Error creating reminder watch rule",
        theme,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setEditForm({
      Name: rule.Name || "",
      CheckTime: toTimePickerValue(rule.CheckTime),
      RecurEveryMinutes: rule.RecurEveryMinutes ?? "",
      Enabled: rule.Enabled !== false,
    });
  };

  const handleSaveEdit = async (ruleId) => {
    const payload = buildPayload(editForm);
    if (!payload) return;

    setSaving(true);
    try {
      await api.put(`/reminderWatchRules/${ruleId}`, payload);
      setEditingId(null);
      showSnackbar("Reminder watch rule updated", theme, "success");
      fetchRules();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || "Error updating reminder watch rule",
        theme,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (rule) => {
    if (togglingId === rule.id) return;
    setTogglingId(rule.id);
    try {
      await api.put(`/reminderWatchRules/${rule.id}`, {
        Enabled: !rule.Enabled,
      });
      showSnackbar(
        rule.Enabled ? "Reminder watch rule disabled" : "Reminder watch rule enabled",
        theme,
        "success",
      );
      fetchRules();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || "Error updating reminder watch rule",
        theme,
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = (rule) => {
    const ruleName = rule.Name || "Reminder Watch";
    openDialog({
      theme,
      title: "Delete Reminder Watch Rule",
      message: `Are you sure you want to delete "${ruleName}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/reminderWatchRules/${rule.id}`);
          closeDialog();
          showSnackbar("Reminder watch rule deleted", theme, "success");
          fetchRules();
        } catch (err) {
          closeDialog();
          showSnackbar(
            err?.response?.data?.error || "Error deleting reminder watch rule",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const formatRecurrence = (rule) => {
    if (rule.RecurEveryMinutes == null) return "Once daily";
    return `Every ${rule.RecurEveryMinutes} min until sent`;
  };

  const formatCheckTime = (rule) => {
    const parsed = toTimePickerValue(rule.CheckTime);
    return parsed.isValid()
      ? `${parsed.format("h:mm A")} EST`
      : `${rule.CheckTime} EST`;
  };

  // Visible field borders — the default outlined border is too faint against
  // the card background, so give the fields a clear border + hover/focus states.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.default,
      borderRadius: "10px",
      "& fieldset": {
        borderColor: theme.palette.primary.border2,
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  };

  const cardSx = {
    bgcolor: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.border2}`,
    borderRadius: "10px",
    p: 1.5,
    transition: "all 0.2s ease",
  };

  const timePickerSlotProps = {
    textField: {
      size: "small",
      sx: { minWidth: isMobile ? "100%" : 170, ...fieldSx },
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
          Reminder Watch
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Reminder Watch checks tomorrow&apos;s interpretation assignments and
          notifies you when an assignment has not been reminded yet. Rules are
          personal to your account.
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Reminder checks run on America/New_York time (EST/EDT).
            </Typography>
            {/* Add rule */}
            <Box
              sx={{
                ...cardSx,
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                size="small"
                label="Name (optional)"
                value={addForm.Name}
                onChange={(event) =>
                  setAddForm((prev) => ({
                    ...prev,
                    Name: event.target.value,
                  }))
                }
                sx={{ flex: "1 1 160px", ...fieldSx }}
              />
              <CETimePicker
                label="Check Time (America/New_York)"
                value={addForm.CheckTime}
                onChange={(newValue) =>
                  setAddForm((prev) => ({ ...prev, CheckTime: newValue }))
                }
                ampm
                slotProps={timePickerSlotProps}
              />
              <TextField
                size="small"
                label="Recur every (min)"
                type="number"
                inputProps={{ min: 5 }}
                placeholder="Leave empty = once daily"
                value={addForm.RecurEveryMinutes}
                onChange={(event) =>
                  setAddForm((prev) => ({
                    ...prev,
                    RecurEveryMinutes: event.target.value,
                  }))
                }
                sx={{ flex: "1 1 150px", ...fieldSx }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={addForm.Enabled}
                    onChange={(event) =>
                      setAddForm((prev) => ({
                        ...prev,
                        Enabled: event.target.checked,
                      }))
                    }
                  />
                }
                label="Enabled"
              />
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleCreate}
                disabled={saving}
              >
                Add Rule
              </Button>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {rules.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No reminder watch rules yet — add one above.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {rules.map((rule) => (
                  <Box
                    key={rule.id}
                    sx={{
                      ...cardSx,
                      "&:hover": {
                        bgcolor: theme.palette.action.hover,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {editingId === rule.id ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          size="small"
                          label="Name (optional)"
                          value={editForm.Name}
                          onChange={(event) =>
                            setEditForm((prev) => ({
                              ...prev,
                              Name: event.target.value,
                            }))
                          }
                          sx={{ flex: "1 1 150px", ...fieldSx }}
                        />
                        <CETimePicker
                          label="Check Time (America/New_York)"
                          value={editForm.CheckTime}
                          onChange={(newValue) =>
                            setEditForm((prev) => ({
                              ...prev,
                              CheckTime: newValue,
                            }))
                          }
                          ampm
                          slotProps={timePickerSlotProps}
                        />
                        <TextField
                          size="small"
                          label="Recur every (min)"
                          type="number"
                          inputProps={{ min: 5 }}
                          placeholder="Leave empty = once daily"
                          value={editForm.RecurEveryMinutes}
                          onChange={(event) =>
                            setEditForm((prev) => ({
                              ...prev,
                              RecurEveryMinutes: event.target.value,
                            }))
                          }
                          sx={{ flex: "1 1 140px", ...fieldSx }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editForm.Enabled}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  Enabled: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Enabled"
                        />
                        <Button
                          variant="contained"
                          onClick={() => handleSaveEdit(rule.id)}
                          disabled={saving}
                        >
                          Save
                        </Button>
                        <Button color="inherit" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {rule.Name || "Reminder Watch"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {formatCheckTime(rule)} • {formatRecurrence(rule)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={rule.Enabled !== false}
                                onChange={() => handleToggleEnabled(rule)}
                                disabled={togglingId === rule.id}
                              />
                            }
                            label={rule.Enabled !== false ? "On" : "Off"}
                            sx={{ mr: 0.5 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => startEdit(rule)}
                            aria-label={`Edit ${rule.Name || "Reminder Watch"}`}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(rule)}
                            aria-label={`Delete ${rule.Name || "Reminder Watch"}`}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReminderWatchSettings;